import { useAuth } from '../stores/auth';

/**
 * Los idiomas que el producto tiene de verdad.
 *
 * Es el mismo par que `User::SUPPORTED_LOCALES` en el backend y que el `enum`
 * de `UserSchema.php`. Si algún día crece, crece en los tres sitios a la vez o
 * el `PATCH /auth/me` devolverá un 422 que la interfaz no sabrá explicar.
 */
export const LOCALES = ['es', 'en'] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'es';

/** Cómo se llama cada idioma en su propio idioma, que es como se listan. */
export const LOCALE_NAMES: Record<Locale, string> = {
  es: 'Español',
  en: 'English',
};

const STORAGE_KEY = 'absor.locale';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * El idioma recordado en `localStorage`.
 *
 * El store de sesión prohíbe el disco a propósito —el token de acceso vive solo
 * en memoria para que un XSS no pueda leerlo— y esto no lo contradice: una
 * preferencia de idioma no es una credencial. Está aquí por dos motivos
 * concretos: el arranque hace `POST /auth/refresh` antes de saber quién eres, y
 * sin este espejo la aplicación entera parpadearía en el idioma equivocado
 * durante esos cientos de milisegundos; y las pantallas de login, verificación
 * y reseteo no tienen sesión de la que leer el idioma.
 */
export function readStoredLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    return isLocale(stored) ? stored : null;
  } catch {
    // Modo privado de Safari y navegadores con el almacenamiento bloqueado: sin
    // memoria, pero la aplicación tiene que arrancar igual.
    return null;
  }
}

export function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* Ver arriba. */
  }
}

/**
 * El idioma efectivo, por orden de autoridad.
 *
 * 1. La preferencia del usuario, siempre que haya sesión: es la que se guardó a
 *    propósito desde la pantalla de cuenta.
 * 2. El espejo en `localStorage`, que es lo que eligió quien todavía no ha
 *    entrado — el conmutador del login escribe ahí.
 * 3. El idioma de la instalación.
 *
 * **El navegador no entra en la lista, y es una decisión.** La primera versión
 * miraba `navigator.languages`, y el resultado era que el dueño de una
 * instalación en español abría el login en inglés porque su portátil está
 * configurado en inglés. Esta es una plataforma privada cuyo idioma lo fija
 * quien la instala —`ABSOR`/`default_locale: es`, `User::$locale = 'es'`— y
 * dejar que lo decidiera el navegador convertía «español por defecto» en «lo que
 * diga el navegador de cada uno». Quien quiera inglés lo pide con el conmutador
 * y se recuerda; quien tenga cuenta lo lleva en su ficha.
 */
export function resolveLocale(userLocale: string | null | undefined): Locale {
  if (isLocale(userLocale)) {
    return userLocale;
  }

  return readStoredLocale() ?? DEFAULT_LOCALE;
}

/** El idioma activo fuera de React: mensajes de error de la capa de API. */
export function currentLocale(): Locale {
  return resolveLocale(useAuth.getState().user?.locale);
}
